import { useCampaignSectionGate } from "features/campaigns";
import { useFavoritesStore } from "features/favorites";
import { useTopWins } from "features/topWins";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGameSounds } from "shared/hooks";
import { useAppStore, useSections } from "store/app/appStore";
import { LOBBY_SECTION_NAME, FAVORITE_SECTION_NAME, HOT_AND_COLD_SECTION_NAME, SECTION_NAMES } from "@repo/common";
import { clickCounter } from "core/services/EventTracker";
import { sectionNavigationTracker } from "core/services/SectionNavigationTracker";

const scrollToTop = (containerDataViewSelector: string, behavior: ScrollBehavior = 'smooth') => {
    const container = document.querySelector(
        `[data-view="${containerDataViewSelector}"][data-section-container]`,
    ) as HTMLElement;

    if (container) {
        container.scrollTo({
            top: 0,
            behavior: behavior,
        });
    }
};

export const useNavigation = () => {
    const { isCampaignEnabled } = useCampaignSectionGate();
    const { sections, selectedSection, viewMode } = useSections();
    const { favorites } = useFavoritesStore();
    const { recent: topWins } = useTopWins();
    const { playSelectTabSound } = useGameSounds();

    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Build the scrollable tabs list from sections + favorites
    const scrollableTabs = useMemo(() => {
        const validSections = sections.filter(section => {
            if (section.name === HOT_AND_COLD_SECTION_NAME) return false;
            const campaignStatus = isCampaignEnabled(section.name);
            if (campaignStatus !== null) return campaignStatus;
            if (section.name === SECTION_NAMES.TOP_WIN_GAMES) {
                // Mirror the multi-view gate (TopWinGamesSection): show the tab only once
                // BE returns enough qualifying wins. Games come from the live snapshot,
                // not section.games.
                const minLimit = section.minLimit != null && section.minLimit >= 1 ? section.minLimit : 1;
                return topWins.length >= minLimit;
            }
            return section.games.length > 0;
        });

        const favoritesTab = {
            name: FAVORITE_SECTION_NAME,
            games: favorites.games,
            icon: 'favorites_icon',
            jackpotName: null,
            banner: false,
            gridBlocks: 0,
        };

        return [...validSections, favoritesTab];
    }, [sections, favorites.games, isCampaignEnabled, topWins.length]);

    // Handle tab click
    const handleTabClick = useCallback(
        (sectionName: string) => {
            if (isTransitioning) return; // Prevent rapid clicks

            setIsTransitioning(true);
            playSelectTabSound();

            const currentState = useAppStore.getState();

            // Determine target view based on clicked lobby footer section.
            const target = {
                selectedSection: sectionName,
                viewMode: sectionName === LOBBY_SECTION_NAME ? 'multi' : 'single',
            } as const;

            // Check if already in the target view - if so, scroll to top
            const isSameView =
                currentState.selectedSection === target.selectedSection && currentState.viewMode === target.viewMode;

            if (isSameView) {
                // Already in target view, scroll to top
                const containerDataViewSelector = target.viewMode === 'multi' ? 'multi' : 'single';
                scrollToTop(containerDataViewSelector);
            } else {
                // Track section navigation via footer (only for single-view sections, not LOBBY multi-view)
                if (target.viewMode === 'single') {
                    sectionNavigationTracker.trackSectionNavigation(target.selectedSection, 'footer_navigation');
                }

                // Switch to target type of view
                useAppStore.setState(() => ({
                    selectedSection: target.selectedSection,
                    viewMode: target.viewMode,
                }));

                // Scroll instantly to top when switching to a new lobby footer section
                scrollToTop('single', 'instant');
            }

            // Reset the transitioning flag on the next frame
            requestAnimationFrame(() => setIsTransitioning(false));
        },
        [isTransitioning, playSelectTabSound],
    );

    // Create tracked version of tab click handler
    const createTrackedTabClick = useCallback(
        (sectionName: string) => {
            return () => {
                // Track with consistent naming: click_navigation_{SECTION_NAME}
                clickCounter.incrementClick(`click_navigation_${sectionName.toUpperCase()}`);
                handleTabClick(sectionName);
            };
        },
        [handleTabClick],
    );

    // Auto-scroll to center active tab
    const scrollToActiveTab = useCallback(() => {
        const container = tabsContainerRef.current;
        const activeTabRef = tabRefs.current.get(selectedSection);

        if (!container || !activeTabRef) return;

        const containerWidth = container.clientWidth;
        const tabLeft = activeTabRef.offsetLeft;
        const tabWidth = activeTabRef.offsetWidth;

        // Calculate scroll position to center the active tab
        const scrollPosition = tabLeft + tabWidth / 2 - containerWidth / 2;

        container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth',
        });
    }, [selectedSection]);

    // Debounced scroll-to-beginning
    const debouncedScrollToBeginning = useMemo(() => {
        let timeoutId: NodeJS.Timeout;
        return () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const container = tabsContainerRef.current;
                if (container) {
                    container.scrollTo({
                        left: 0,
                        behavior: 'smooth',
                    });
                }
            }, 16); // One frame delay
        };
    }, []);

    // Track if component just mounted
    const [hasMounted, setHasMounted] = useState(false);

    // Scroll to beginning only on initial mount
    useEffect(() => {
        if (!hasMounted && scrollableTabs.length > 1) {
            debouncedScrollToBeginning();
            setHasMounted(true);
        }
    }, [hasMounted, scrollableTabs.length, debouncedScrollToBeginning]);

    // Auto-scroll when selected section changes
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (selectedSection !== LOBBY_SECTION_NAME && viewMode === 'single') {
            // Small delay to ensure tab refs are available when using openSection param
            timeoutId = setTimeout(() => {
                scrollToActiveTab();
            }, 50);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [selectedSection, viewMode, scrollToActiveTab]);

    // Scroll to beginning when returning to lobby (multi-view)
    useEffect(() => {
        if (viewMode === 'multi' || selectedSection === LOBBY_SECTION_NAME) {
            debouncedScrollToBeginning();
        }
    }, [viewMode, selectedSection, debouncedScrollToBeginning]);

    return {
        tabsContainerRef,
        tabRefs,
        scrollableTabs,
        selectedSection,
        viewMode,
        isTransitioning,
        createTrackedTabClick
    };
};
